<template>
	<v-menu
		v-model="menu"
		:close-on-content-click="false"
		offset-y
		bottom
		left
		:min-width="menuWidth"
		:max-width="menuWidth"
		content-class="product-switcher-menu"
	>
		<template #activator="{ on: menuOn, attrs: menuAttrs }">
			<v-tooltip bottom>
				<template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
					<v-btn
						v-bind="{ ...tooltipAttrs, ...menuAttrs }"
						icon
						color="primary"
						class="ml-2 product-switch-button"
						aria-label="Switch product"
						:disabled="loading"
						v-on="{ ...tooltipOn, ...menuOn }"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
							class="lucide lucide-grid3x3"
							aria-hidden="true"
							focusable="false"
						>
							<rect
								width="18"
								height="18"
								x="3"
								y="3"
								rx="2"
							/>
							<path d="M3 9h18" />
							<path d="M3 15h18" />
							<path d="M9 3v18" />
							<path d="M15 3v18" />
						</svg>
						<v-progress-circular
							v-if="loading"
							indeterminate
							size="16"
							class="ml-1"
						/>
					</v-btn>
				</template>
				<span>
					Switch product
				</span>
			</v-tooltip>
		</template>

		<v-card class="product-switcher-card">
			<v-card-text class="pa-0">
				<div
					class="portal-grid"
					:style="{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }"
				>
					<div
						v-for="(portal, index) in portals"
						:key="portal.name"
						:ref="index === 0 ? 'firstPortalCard' : undefined"
						class="portal-card"
						:class="{
							'portal-card--disabled': portal.disabled,
							'portal-card--active': isCurrentPortal(portal)
						}"
						:tabindex="portal.disabled || isCurrentPortal(portal) ? -1 : 0"
						role="button"
						:aria-label="isCurrentPortal(portal) ? `${portal.name} (Current)` : `Navigate to ${portal.name}`"
						@click="navigateToPortal(portal)"
						@keydown.enter="navigateToPortal(portal)"
						@keydown.space.prevent="navigateToPortal(portal)"
					>
						<div
							v-if="isCurrentPortal(portal)"
							class="portal-current-chip"
						>
							Current
						</div>
						<div class="portal-card-content">
							<div class="portal-icon-wrapper">
								<v-icon
									color="primary"
									size="28"
								>
									{{ portal.icon }}
								</v-icon>
							</div>
							<h3 class="portal-name">
								{{ portal.name }}
							</h3>
							<p
								v-if="portal.description"
								class="portal-description"
							>
								{{ portal.description }}
							</p>
							<div
								v-if="!isCurrentPortal(portal)"
								class="portal-actions"
							>
								<v-icon
									size="18"
									color="grey"
									class="ml-1"
								>
									mdi-chevron-right
								</v-icon>
							</div>
						</div>
					</div>
				</div>
			</v-card-text>
		</v-card>
	</v-menu>
</template>

<script>
import { mapGetters } from 'vuex'
import { createExchangeTokenRedirect } from '../../admin-portal/src/services/exchangeTokenService.js'
import { TARGET_APPS } from '../enums/target-apps.js'
import { navigateToDashboardPortal, EXCHANGE_TOKEN_REDIRECT_ERROR_MESSAGE } from '../utils/navigation/dashboard-navigation.js'
import { showSnackbar } from '../state/snackbar.js'
import { privacyNoticeModuleEnabled, nextGenBannerEnabled, mobileConsentEnabled } from '../state/config-keys.js'
import {
	hasRiskPortalAccess as checkRiskPortalAccess,
	hasPrivacyNoticesAccess as checkPrivacyNoticesAccess,
	hasConsentBannersAccess as checkConsentBannersAccess,
	hasMobileConsentAccess as checkMobileConsentAccess
} from '../utils/product-access.js'

export default {
	name: 'product-switch-button',
	setup () {
		return {
			privacyNoticeModuleEnabled,
			nextGenBannerEnabled,
			mobileConsentEnabled
		}
	},
	data () {
		return {
			menu: false,
			loading: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		/**
		 * Check if user has at least one Risk Portal permission
		 * @returns {boolean} True if user has any risk portal permission
		 */
		hasRiskPortalAccess () {
			return checkRiskPortalAccess(this.productAreaPermission)
		},
		/**
		 * Check if user has Privacy Notices access (config key enabled AND has permission)
		 * @returns {boolean} True if user can access Privacy Notices
		 */
		hasPrivacyNoticesAccess () {
			return checkPrivacyNoticesAccess(this.productAreaPermission, this.privacyNoticeModuleEnabled)
		},
		/**
		 * Check if user has Consent Banners access (config key enabled AND has permission)
		 * @returns {boolean} True if user can access Consent Banners
		 */
		hasConsentBannersAccess () {
			return checkConsentBannersAccess(this.productAreaPermission, this.nextGenBannerEnabled)
		},
		/**
		 * Check if user has Mobile Consent access (config key enabled AND has permission)
		 * Mobile Consent reuses the Consent Banners permission set until dedicated permissions exist
		 * @returns {boolean} True if user can access Mobile Consent
		 */
		hasMobileConsentAccess () {
			return checkMobileConsentAccess(this.productAreaPermission, this.mobileConsentEnabled)
		},
		portals () {
			// Descriptions match splash page OverviewCards exactly
			const allPortals = [
				{
					name: 'Administration Portal',
					description: 'Manage users, system settings and core configuration',
					icon: 'mdi-cog-outline',
					internalRoute: '/dashboard'
				},
				{
					name: 'Risk Portal',
					description: 'Identify and manage your risk exposure',
					icon: 'mdi-shield-check-outline',
					targetAppKey: TARGET_APPS.RISK,
					requiresPermission: 'riskPortal'
				},
				{
					name: 'Privacy Notices',
					description: 'Create and manage privacy notices for your organization',
					icon: 'mdi-note-text-outline',
					targetAppKey: TARGET_APPS.NOTICES,
					requiresPermission: 'privacyNotices'
				},
				{
					name: 'Consent Banners',
					description: 'Create and manage consent banners for your organization',
					icon: 'mdi-cookie-outline',
					targetAppKey: TARGET_APPS.DASHBOARD,
					requiresPermission: 'consentBanners'
				},
				{
					name: 'Mobile Consent',
					description: 'Manage mobile consent containers and view mobile SDK analytics',
					icon: 'mdi-cellphone',
					targetAppKey: TARGET_APPS.MOBILE_CONSENT,
					requiresPermission: 'mobileConsent'
				}
			]
			return allPortals.filter(portal => {
				if (portal.requiresPermission === 'riskPortal') {
					return this.hasRiskPortalAccess
				}
				if (portal.requiresPermission === 'privacyNotices') {
					return this.hasPrivacyNoticesAccess
				}
				if (portal.requiresPermission === 'consentBanners') {
					return this.hasConsentBannersAccess
				}
				if (portal.requiresPermission === 'mobileConsent') {
					return this.hasMobileConsentAccess
				}
				return true
			})
		},
		menuWidth () {
			// Calculate width based on number of portals (capped at 3 per row)
			// Card width: ~270px, gap: 12px, padding: 24px
			const cardWidth = 270
			const gap = 12
			const padding = 24
			const count = Math.min(this.portals.length, 3)

			if (count <= 1) return cardWidth + padding
			if (count === 2) return (cardWidth * 2) + gap + padding
			return (cardWidth * 3) + (gap * 2) + padding
		},
		gridColumns () {
			// Return appropriate grid columns based on portal count (capped at 3 per row)
			const count = this.portals.length
			if (count <= 1) return 1
			if (count === 2) return 2
			return 3
		}
	},
	watch: {
		menu (newVal) {
			if (newVal) {
				// Focus first portal card when menu opens
				// Use setTimeout to ensure v-menu has fully rendered
				this.$nextTick(() => {
					setTimeout(() => {
						// Try using ref first
						if (this.$refs.firstPortalCard) {
							const cardElement = Array.isArray(this.$refs.firstPortalCard)
								? this.$refs.firstPortalCard[0]
								: this.$refs.firstPortalCard
							const el = cardElement?.$el || cardElement
							if (el && el.focus) {
								el.focus()
								return
							}
						}
						// Fallback to querySelector
						const firstCard = document.querySelector('.portal-card:not([disabled])')
						if (firstCard) {
							firstCard.focus()
						}
					}, 150)
				})
			}
		}
	},
	methods: {
		isCurrentPortal (portal) {
			// Administration Portal is the current portal in this app
			return portal.name === 'Administration Portal'
		},
		async navigateToPortal (portal) {
			if (!portal || portal.disabled || this.loading || this.isCurrentPortal(portal)) return

			// Handle internal routes (e.g., Administration Portal -> /dashboard)
			if (portal.internalRoute) {
				this.menu = false
				this.$router.push(portal.internalRoute)
				return
			}

			this.loading = true
			let redirecting = false
			try {
				if (portal.targetAppKey === TARGET_APPS.DASHBOARD) {
					redirecting = await navigateToDashboardPortal()
					return
				}
				const redirectUrl = await createExchangeTokenRedirect(portal.targetAppKey)
				if (!redirectUrl) {
					showSnackbar({ text: EXCHANGE_TOKEN_REDIRECT_ERROR_MESSAGE, color: 'red' })
					return
				}
				window.location.href = redirectUrl
			} catch (e) {
				console.error('Error navigating to portal:', e)
				showSnackbar({ text: EXCHANGE_TOKEN_REDIRECT_ERROR_MESSAGE, color: 'red' })
			} finally {
				if (!redirecting) {
					this.loading = false
					this.menu = false
				}
			}
		}
	}
}
</script>

<style scoped>
.product-switch-button {
	text-transform: none;
	letter-spacing: 0;
}

.product-switch-button .lucide-grid3x3 {
	color: var(--global-header-text-color, #637083);
}

.product-switcher-card {
	display: flex;
	background-color: var(--global-card-background, #ffffff);
}

.product-switcher-card >>> .v-card__text {
	display: flex;
	justify-content: center;
	width: 100%;
}

.portal-grid {
	display: grid;
	/* grid-template-columns applied dynamically via inline style */
	gap: 12px;
	padding: 12px;
	width: 100%;
}

.portal-card {
	position: relative;
	border-radius: var(--radius, 8px);
	border: 1px solid var(--global-border-color, #e5e7eb);
	border-left: 4px solid var(--global-primary, #406A94);
	background-color: var(--global-card-background, #ffffff);
	cursor: pointer;
	transition: all 0.2s ease;
	outline: none;
	min-width: 250px;
}

.portal-card:hover:not(.portal-card--disabled):not(.portal-card--active) {
	/* transform: translateY(-2px); */
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.portal-card:focus:not(.portal-card--disabled) {
	box-shadow: 0 0 0 2px var(--global-primary, #406A94);
}

.portal-card--disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.portal-card--active {
	cursor: default;
	background-color: #ebedf080 !important;
}

.portal-card--active:hover {
	transform: none !important;
	box-shadow: none !important;
	background-color: #ebedf080 !important;
}

.portal-current-chip {
	position: absolute;
	top: 20px;
	right: 20px;
	padding: 2px 8px;
	font-size: 0.625rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #ffffff;
	background-color: var(--global-primary, #406A94);
	border-radius: 4px;
	line-height: 1.4;
}

.portal-card-content {
	padding: 20px;
	display: flex;
	flex-direction: column;
	min-height: 140px;
}

.portal-icon-wrapper {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	background-color: var(--global-muted, #f3f4f6);
	border-radius: var(--radius, 8px);
	margin-bottom: 16px;
}

.portal-name {
	font-family: 'Inter', sans-serif;
	font-size: 0.875rem;
	font-weight: 600;
	color: var(--global-header-text-color, #1f2937);
	margin: 0 0 8px 0;
	line-height: 1.4;
}

.portal-description {
	font-family: 'Inter', sans-serif;
	font-size: 0.75rem;
	font-weight: 400;
	color: var(--global-muted-foreground, #6b7280);
	margin: 0;
	line-height: 1.5;
	flex-grow: 1;
}

.portal-actions {
	position: absolute;
	bottom: 16px;
	right: 16px;
	display: flex;
	align-items: center;
	gap: 2px;
}
</style>

<!-- Unscoped styles for dark mode and menu dropdown -->
<style>
.product-switcher-menu {
	margin-top: 8px;
	background-color: var(--global-card-background, #ffffff);
	border-radius: var(--radius, 8px);
	border: 1px solid var(--global-border-color, #e5e7eb);
}

/* Dark mode overrides */
.dark .product-switcher-card,
.theme--dark .product-switcher-card {
	background-color: var(--global-card-background, #1c1c1e) !important;
}

.dark .product-switcher-menu,
.theme--dark .product-switcher-menu {
	background-color: var(--global-card-background, #1c1c1e);
	border-color: var(--global-border-color, #2e2e32);
}

.dark .portal-card,
.theme--dark .portal-card {
	background-color: var(--global-card-background, #1c1c1e) !important;
	border-top: 1px solid var(--global-border-color, #2e2e32) !important;
	border-right: 1px solid var(--global-border-color, #2e2e32) !important;
	border-bottom: 1px solid var(--global-border-color, #2e2e32) !important;
	border-left-width: 4px !important;
	border-left-style: solid !important;
	border-left-color: var(--global-primary, #ed303e) !important;
}

.dark .portal-card:hover:not(.portal-card--disabled):not(.portal-card--active),
.theme--dark .portal-card:hover:not(.portal-card--disabled):not(.portal-card--active) {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.dark .portal-card--active,
.theme--dark .portal-card--active {
	background-color: rgba(255, 255, 255, 0.12) !important;
}

.dark .portal-card--active:hover,
.theme--dark .portal-card--active:hover {
	transform: none !important;
	box-shadow: none !important;
	background-color: rgba(255, 255, 255, 0.12) !important;
}

.dark .portal-current-chip,
.theme--dark .portal-current-chip {
	background-color: var(--global-primary, #ed303e);
}

.dark .portal-card:focus:not(.portal-card--disabled),
.theme--dark .portal-card:focus:not(.portal-card--disabled) {
	box-shadow: 0 0 0 2px var(--global-primary, #ed303e) !important;
	outline: none;
}

.dark .portal-icon-wrapper,
.theme--dark .portal-icon-wrapper {
	background-color: rgba(255, 255, 255, 0.08) !important;
}

.dark .portal-name,
.theme--dark .portal-name {
	color: var(--global-foreground, #f5f5f5) !important;
}

.dark .portal-description,
.theme--dark .portal-description {
	color: var(--global-muted-foreground, #9ca3af) !important;
}

.dark .product-switch-button .lucide-grid3x3,
.theme--dark .product-switch-button .lucide-grid3x3 {
	color: var(--global-foreground, #ffffff) !important;
}
</style>
